/**
 * CommentsModerationPanel.js — Phase I
 * Tech Specialist UI to moderate WordPress comments without wp-admin.
 *
 * Backend: /api/wp-comments/{org_id}?status=hold|approve|spam|trash
 *          PATCH /api/wp-comments/{org_id}/{id}  body: {status: ...}
 *          DELETE /api/wp-comments/{org_id}/{id}  (permanent — after trash)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import LiveSiteHeader from './LiveSiteHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TABS = [
  { key: 'hold',    label: 'Pending' },
  { key: 'approve', label: 'Approved' },
  { key: 'spam',    label: 'Spam' },
  { key: 'trash',   label: 'Trash' },
];

export default function CommentsModerationPanel({ orgId }) {
  const [status, setStatus] = useState('hold');
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bridgeMissing, setBridgeMissing] = useState(false);
  const [wpDown, setWpDown] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true); setBridgeMissing(false); setWpDown(false);
    try {
      const { data } = await axios.get(`${API}/wp-comments/${orgId}?status=${status}`, { withCredentials: true });
      const list = Array.isArray(data) ? data : (data.comments || data.items || []);
      setComments(list);
    } catch (e) {
      const d = e.response?.data?.detail || '';
      if (e.response?.status === 503) setWpDown(true);
      else if (e.response?.status === 502 && /bridge_lacks/.test(d)) setBridgeMissing(true);
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [orgId, status]);

  useEffect(() => { load(); }, [load]);

  const setCommentStatus = async (id, newStatus) => {
    await axios.patch(`${API}/wp-comments/${orgId}/${id}`, { status: newStatus }, { withCredentials: true });
    await load();
  };
  const purge = async (id) => {
    if (!window.confirm('Permanently delete this comment? Cannot be undone.')) return;
    await axios.delete(`${API}/wp-comments/${orgId}/${id}`, { withCredentials: true });
    await load();
  };

  return (
    <div data-testid="comments-panel" className="p-6 space-y-4">
      <LiveSiteHeader orgId={orgId} surfaceLabel="Comments moderation" />
      <div>
        <h2 className="text-lg font-semibold">Comments Moderation</h2>
        <p className="text-sm text-gray-500">Approve, mark as spam, or trash WordPress comments — no wp-admin login required.</p>
      </div>

      <div className="flex gap-2 border-b">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`comments-tab-${t.key}`}
            onClick={() => setStatus(t.key)}
            className={`px-4 py-2 text-sm border-b-2 ${
              status === t.key ? 'border-blue-600 text-blue-600 font-medium' : 'border-transparent text-gray-500'
            }`}
          >{t.label}</button>
        ))}
      </div>

      {wpDown && <Banner tone="red" title="WordPress unreachable" body="Re-toggle the Live Link tunnel." onRetry={load} />}
      {bridgeMissing && <Banner tone="amber" title="Bridge plugin update needed" body="The bridge plugin doesn't expose /comments. Ship docs/wp-bridge-extensions/COMMENTS.md." onRetry={load} />}

      {!wpDown && !bridgeMissing && (
        <>
          {loading && <div className="text-gray-500 text-sm">Loading…</div>}
          {!loading && comments.length === 0 && (
            <div className="text-gray-500 text-sm py-4" data-testid="comments-empty">
              No {status === 'hold' ? 'pending' : status} comments.
            </div>
          )}
          <ul className="divide-y border rounded" data-testid="comments-list">
            {comments.map((c) => (
              <li key={c.id} className="px-4 py-3" data-testid={`comment-${c.id}`}>
                <div className="flex justify-between items-start gap-3 mb-1">
                  <div>
                    <div className="font-medium text-sm">{c.author_name || c.author || '(unknown)'}</div>
                    <div className="text-xs text-gray-500">
                      {c.author_email && `${c.author_email} · `}
                      {c.date && new Date(c.date).toLocaleString()}
                      {c.post_id && ` · post #${c.post_id}`}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {status !== 'approve' && <button data-testid={`comment-approve-${c.id}`} onClick={() => setCommentStatus(c.id, 'approve')} className="px-2 py-1 text-xs text-green-700 border border-green-200 rounded hover:bg-green-50">Approve</button>}
                    {status !== 'hold' && status !== 'spam' && status !== 'trash' && <button data-testid={`comment-hold-${c.id}`} onClick={() => setCommentStatus(c.id, 'hold')} className="px-2 py-1 text-xs text-amber-700 border border-amber-200 rounded hover:bg-amber-50">Hold</button>}
                    {status !== 'spam' && <button data-testid={`comment-spam-${c.id}`} onClick={() => setCommentStatus(c.id, 'spam')} className="px-2 py-1 text-xs text-orange-700 border border-orange-200 rounded hover:bg-orange-50">Spam</button>}
                    {status !== 'trash' && <button data-testid={`comment-trash-${c.id}`} onClick={() => setCommentStatus(c.id, 'trash')} className="px-2 py-1 text-xs text-gray-700 border border-gray-200 rounded hover:bg-gray-50">Trash</button>}
                    {status === 'trash' && <button data-testid={`comment-delete-${c.id}`} onClick={() => purge(c.id)} className="px-2 py-1 text-xs text-red-700 border border-red-200 rounded hover:bg-red-50">Delete forever</button>}
                  </div>
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap"
                     dangerouslySetInnerHTML={{ __html: c.content?.rendered || c.content || c.body || '' }} />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function Banner({ tone, title, body, onRetry }) {
  const cls = tone === 'red' ? 'bg-red-50 border-red-200 text-red-900' : 'bg-amber-50 border-amber-200 text-amber-900';
  const btn = tone === 'red' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700';
  return (
    <div className={`p-4 rounded border ${cls}`}>
      <strong>{title}</strong>
      <p className="mt-1 text-sm">{body}</p>
      <button onClick={onRetry} className={`mt-2 px-3 py-1 text-white rounded text-xs ${btn}`}>Retry</button>
    </div>
  );
}
